import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Checkbox,
    ModalDialog,
    SearchBar,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { AlreadyHeldMention } from './AlreadyHeldMention';
import OverlayLoader from '../OverlayLoader/OverlayLoader';

import { useGetCompetencyGroups } from '@/api/competency-services/competency';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { ChevronDownIcon, ChevronUpIcon } from '@/pages/Competencies/components/icons';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

type AddCompetenciesModalProps = {
    /** The selector mounts this dialog only while competencies are being added, so it opens by default. */
    isOpen?: boolean;
    // Competency ids already in the profile — shown but locked (can't be added again).
    lockedCompetencyIds: string[];
    /** The competencies the employee already holds in their current profile: said, still offered. */
    heldCompetencyIds?: ReadonlySet<string>;
    // Currently added (overlay) competency ids, pre-checked.
    selectedCompetencyIds: string[];
    onConfirm: (competencyIds: string[]) => void;
    onCancel: () => void;
    profileName?: Record<string, string>;
};

// Grouped competency catalog with search, collapsible groups and checkboxes, used by the
// profile selector to ADD extra competencies for one employee.
export const AddCompetenciesModal = ({
    isOpen = true,
    lockedCompetencyIds,
    heldCompetencyIds,
    selectedCompetencyIds,
    onConfirm,
    onCancel,
    profileName,
}: AddCompetenciesModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { data: groupsData, isLoading: groupsLoading } = useGetCompetencyGroups(1, 500);

    const groups = useMemo(() => groupsData ?? [], [groupsData]);
    const lockedSet = useMemo(() => new Set(lockedCompetencyIds), [lockedCompetencyIds]);
    const [selected, setSelected] = useState<Set<string>>(() => new Set(selectedCompetencyIds));
    const [search, setSearch] = useState('');
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

    const toggle = (competencyId: string) => {
        if (lockedSet.has(competencyId)) {
            return;
        }

        setSelected((current) => {
            const next = new Set(current);
            if (next.has(competencyId)) {
                next.delete(competencyId);
            } else {
                next.add(competencyId);
            }

            return next;
        });
    };

    const toggleGroup = (competencyIds: string[]) => {
        const unaddedIds = competencyIds.filter((id) => !lockedSet.has(id));
        if (unaddedIds.length === 0) {
            return;
        }

        setSelected((current) => {
            const next = new Set(current);
            const allSelected = unaddedIds.every((id) => next.has(id));

            if (allSelected) {
                unaddedIds.forEach((id) => next.delete(id));
            } else {
                unaddedIds.forEach((id) => next.add(id));
            }

            return next;
        });
    };

    const toggleCollapse = (groupId: string) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const handleCancel = () => {
        setSearch('');
        setSelected(new Set(selectedCompetencyIds));
        onCancel();
    };

    // Only Active competencies are offered; the search matches name + description.
    const filteredGroups = useMemo(() => {
        const query = search.trim().toLowerCase();

        return groups
            .map((group) => ({
                group,
                competencies: group.competencies.filter((competency) => {
                    if (competency.status !== CompetencyStatus.ACTIVE) {
                        return false;
                    }

                    if (!query) {
                        return true;
                    }

                    const haystack = `${getTextByLanguage(
                        competency.name,
                        language,
                    )} ${getTextByLanguage(competency.description, language)}`.toLowerCase();

                    return haystack.includes(query);
                }),
            }))
            .filter((entry) => entry.competencies.length > 0);
    }, [groups, search, language]);

    const title = profileName
        ? t('competencies.competencyProfilesAdmin.addModal.titleWithProfile', {
              profile: getTextByLanguage(profileName, language),
          })
        : t('competencies.competencyProfilesAdmin.addModal.title');

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={handleCancel}
            closeAriaLabel={t('common.close')}
            title={title}
            subtitle={t('competencies.competencyProfilesAdmin.addModal.subtitle')}
            size={'lg'}
            backdropBlur
            dataTestId={'roster-add-modal'}
            headerExtra={
                <SearchBar
                    size={'md'}
                    value={search}
                    onChange={(value) => setSearch(value)}
                    placeholder={t(
                        'competencies.competencyProfilesAdmin.addModal.searchPlaceholder',
                    )}
                    dataTestId={'roster-add-search'}
                />
            }
            footer={
                <div className={'flex items-center justify-end gap-3'}>
                    <ButtonSecondary
                        size={'md'}
                        onClick={handleCancel}
                        dataTestId={'roster-add-cancel'}
                        fitContent
                    >
                        {t('competencies.competencyProfilesAdmin.addModal.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        size={'md'}
                        disabled={selected.size === 0}
                        onClick={() => onConfirm(Array.from(selected))}
                        dataTestId={'roster-add-confirm'}
                        fitContent
                    >
                        {t('competencies.competencyProfilesAdmin.addModal.addSelection')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            {groupsLoading ? (
                <div className={'flex items-center justify-center py-12'}>
                    <OverlayLoader isLoading={true} />
                </div>
            ) : filteredGroups.length === 0 ? (
                <p className={'text-base-500 py-12 text-center text-base'}>
                    {t('competencies.competencyProfilesAdmin.addModal.empty')}
                </p>
            ) : (
                <div className={'flex flex-col gap-4'}>
                    {filteredGroups.map(({ group, competencies }) => {
                        const isCollapsed = collapsedGroups.has(group.id);
                        const unaddedInGroup = competencies.filter((c) => !lockedSet.has(c.id));
                        const allGroupSelected =
                            unaddedInGroup.length > 0 &&
                            unaddedInGroup.every((c) => selected.has(c.id));
                        const someGroupSelected = unaddedInGroup.some((c) => selected.has(c.id));
                        const isPartial = someGroupSelected && !allGroupSelected;
                        const allAlreadyAdded =
                            competencies.length > 0 &&
                            competencies.every((c) => lockedSet.has(c.id));

                        return (
                            <div
                                key={group.id}
                                className={
                                    'ds-rounded-sm overflow-hidden border border-gray-200 bg-white px-2'
                                }
                            >
                                <div className={'ds-gap-lg ds-px-lg ds-py-lg flex items-center'}>
                                    <div
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    >
                                        <Checkbox
                                            checked={allGroupSelected || allAlreadyAdded}
                                            indeterminate={isPartial}
                                            disabled={
                                                allAlreadyAdded || unaddedInGroup.length === 0
                                            }
                                            ariaLabel={getTextByLanguage(group.name, language)}
                                            onChange={() =>
                                                toggleGroup(unaddedInGroup.map((c) => c.id))
                                            }
                                            dataTestId={`roster-add-group-checkbox-${group.id}`}
                                        />
                                    </div>

                                    <button
                                        type={'button'}
                                        onClick={() => toggleCollapse(group.id)}
                                        className={
                                            'ds-gap-lg flex cursor-pointer flex-col items-center text-left sm:flex-row'
                                        }
                                        data-testid={`roster-add-group-toggle-${group.id}`}
                                    >
                                        <div className={'min-w-0 flex-1'}>
                                            <p className={'text-body text-navy font-semibold'}>
                                                {getTextByLanguage(group.name, language)}
                                            </p>

                                            <p className={'text-body-sm text-navy/60 mt-1'}>
                                                {getTextByLanguage(group.description, language)}
                                            </p>
                                        </div>

                                        <span
                                            className={
                                                'text-caption bg-secondary-25 text-secondary-700 flex flex-shrink-0 items-center rounded-full px-3 py-1 font-medium'
                                            }
                                        >
                                            {t(
                                                'competencies.competencyProfilesAdmin.selector.groupCount',
                                                { count: competencies.length },
                                            )}
                                        </span>
                                        <span>
                                            {isCollapsed ? (
                                                <ChevronDownIcon className={'h-3.5 w-3.5'} />
                                            ) : (
                                                <ChevronUpIcon className={'h-3.5 w-3.5'} />
                                            )}
                                        </span>
                                    </button>
                                </div>

                                {!isCollapsed && (
                                    <div
                                        className={
                                            'ds-px-lg ds-pt-sm ds-pb-md flex flex-col gap-1 border-t border-gray-100'
                                        }
                                    >
                                        {competencies.map((competency) => {
                                            const isLocked = lockedSet.has(competency.id);
                                            const isPending = selected.has(competency.id);

                                            const onRowActivate = () => {
                                                if (!isLocked) {
                                                    toggle(competency.id);
                                                }
                                            };

                                            return (
                                                <div
                                                    key={competency.id}
                                                    role={'button'}
                                                    tabIndex={isLocked ? -1 : 0}
                                                    aria-disabled={isLocked}
                                                    onClick={onRowActivate}
                                                    onKeyDown={(event) => {
                                                        if (
                                                            !isLocked &&
                                                            (event.key === 'Enter' ||
                                                                event.key === ' ')
                                                        ) {
                                                            event.preventDefault();
                                                            onRowActivate();
                                                        }
                                                    }}
                                                    className={
                                                        'ds-rounded-sm ds-p-md flex items-center gap-3 ' +
                                                        (isLocked
                                                            ? 'cursor-not-allowed opacity-70'
                                                            : 'cursor-pointer hover:bg-gray-50')
                                                    }
                                                    data-testid={`roster-add-competency-${competency.id}`}
                                                >
                                                    <div
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                    >
                                                        <Checkbox
                                                            checked={isLocked || isPending}
                                                            disabled={isLocked}
                                                            ariaLabel={getTextByLanguage(
                                                                competency.name,
                                                                language,
                                                            )}
                                                            onChange={() =>
                                                                !isLocked && toggle(competency.id)
                                                            }
                                                            dataTestId={`roster-add-competency-checkbox-${competency.id}`}
                                                        />
                                                    </div>

                                                    <p
                                                        className={
                                                            'text-body-sm text-navy min-w-0 flex-1 font-normal'
                                                        }
                                                    >
                                                        {getTextByLanguage(
                                                            competency.name,
                                                            language,
                                                        )}
                                                    </p>

                                                    {isLocked && (
                                                        <span
                                                            className={
                                                                'text-caption text-base-500 flex-shrink-0'
                                                            }
                                                        >
                                                            {t(
                                                                'competencies.competencyProfilesAdmin.addModal.inProfile',
                                                            )}
                                                        </span>
                                                    )}

                                                    {!isLocked &&
                                                        heldCompetencyIds?.has(competency.id) && (
                                                            <AlreadyHeldMention />
                                                        )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </ModalDialog>
    );
};
